import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useLazyQuery, useMutation } from '@apollo/client'
import dayjs from 'dayjs'

import { Alert, Button, Checkbox, Descriptions, Divider, Empty, Input, Modal, Space, Spin, Table, Tag, Typography } from 'antd'
import { TagsOutlined } from '@ant-design/icons'

import { formatNumberWithDash } from '../../../../../utilties/number'
import notificationActions from '../../../../../redux/notifications/actions'
import inquiryActions from '../../../../../redux/inquiry/actions'

import { APPLY_ONLINE_DISCOUNT_MUTATION, ONLINE_DISCOUNT_PREVIEW_QUERY } from './query'

const { setNotifications } = notificationActions
const { fetchInquiry, fetchInquiryLineItems } = inquiryActions

const { Text } = Typography

const SOURCE_COLORS = {
    scalability: 'blue',
    clearance: 'volcano',
    item_bundle: 'purple'
}

const baht = value => `฿${formatNumberWithDash(Number(value || 0))}`

const ApplyOnlineDiscount = ({ orderId }) => {
    const { t } = useTranslation('inquiries')
    const dispatch = useDispatch()

    const [open, setOpen] = useState(false)
    const [voucherCode, setVoucherCode] = useState('')
    const [applyLineItemPrices, setApplyLineItemPrices] = useState(true)
    const [applyOrderDiscount, setApplyOrderDiscount] = useState(true)

    const [loadPreview, { data, loading }] = useLazyQuery(ONLINE_DISCOUNT_PREVIEW_QUERY, {
        fetchPolicy: 'network-only'
    })

    const [applyOnlineDiscount, { loading: applying }] = useMutation(APPLY_ONLINE_DISCOUNT_MUTATION, {
        onCompleted: ({ applyOrderOnlineDiscount: { errors } }) => {
            if (errors) {
                // `errors` is a JSON blob from the mutation; i18next falls back
                // to the raw string when it is not an `alerts:` key.
                dispatch(setNotifications('saveFail', errors, 'error'))

                return
            }

            // The mutation rewrites line item prices and the order discount, and
            // the order totals are recalculated server side, so both have to be
            // pulled again rather than patched in place.
            dispatch(fetchInquiry(orderId))
            dispatch(fetchInquiryLineItems(orderId))
            dispatch(setNotifications('success', 'saveSuccess', 'success'))
            setOpen(false)
        }
    })

    const preview = data && data.orderOnlineDiscountPreview

    const openModal = () => {
        setOpen(true)
        loadPreview({ variables: { orderId, voucherCode: voucherCode || null } })
    }

    const recheck = () => {
        loadPreview({ variables: { orderId, voucherCode: voucherCode || null } })
    }

    const onApply = () => {
        applyOnlineDiscount({
            variables: {
                orderId,
                voucherCode: voucherCode || null,
                applyLineItemPrices,
                applyOrderDiscount
            }
        })
    }

    const columns = [
        {
            title: t('onlineDiscount.item'),
            dataIndex: 'name',
            render: (name, record) => (
                <Space direction='vertical' size={0}>
                    <Text>{name}</Text>
                    <Space size={4} wrap>
                        {(record.discountSources || []).map(source => (
                            <Tag color={SOURCE_COLORS[source]} key={source}>
                                {t(`onlineDiscount.sources.${source}`)}
                            </Tag>
                        ))}
                        {record.excludedFromDiscountLadder &&
                            <Tag>{t('onlineDiscount.excludedFromLadder')}</Tag>}
                    </Space>
                </Space>
            )
        },
        {
            title: t('onlineDiscount.quantity'),
            dataIndex: 'quantity',
            align: 'right',
            width: 80
        },
        {
            title: t('onlineDiscount.currentPrice'),
            dataIndex: 'currentSinglePrice',
            align: 'right',
            width: 120,
            render: value => baht(value)
        },
        {
            title: t('onlineDiscount.onlinePrice'),
            dataIndex: 'onlineSinglePrice',
            align: 'right',
            width: 120,
            render: (value, record) => (
                <Text type={record.difference > 0 ? 'success' : undefined}>{baht(value)}</Text>
            )
        },
        {
            title: t('onlineDiscount.difference'),
            dataIndex: 'difference',
            align: 'right',
            width: 120,
            render: value => {
                if (!value) return <Text type='secondary'>-</Text>

                // A negative difference means the online price is HIGHER than
                // what the sale rep already quoted; applying would raise it.
                return <Text type={value > 0 ? 'success' : 'danger'}>{baht(value)}</Text>
            }
        }
    ]

    const renderLadder = ladder => {
        if (!ladder) return <Alert type='info' showIcon message={t('onlineDiscount.noActiveLadder')} />

        return (
            <Descriptions bordered size='small' column={1}
                title={t('onlineDiscount.discountLadder')}>
                <Descriptions.Item label={t('onlineDiscount.campaign')}>
                    {ladder.campaignName || ladder.description || '-'}
                    {ladder.expireDate &&
                        <Text type='secondary'>
                            {` (${t('onlineDiscount.until')} ${dayjs(ladder.expireDate).format('D MMM YYYY')})`}
                        </Text>}
                </Descriptions.Item>
                <Descriptions.Item label={t('onlineDiscount.eligibleItemsTotal')}>
                    {baht(ladder.eligibleItemsTotal)}
                </Descriptions.Item>
                <Descriptions.Item label={t('onlineDiscount.currentTier')}>
                    {ladder.currentTier
                        ? t('onlineDiscount.tierLabel', {
                            minimum: formatNumberWithDash(ladder.currentTier.minimumPrice),
                            discount: formatNumberWithDash(ladder.currentTier.discountPrice)
                        })
                        : t('onlineDiscount.noTierReached')}
                </Descriptions.Item>
                <Descriptions.Item label={t('onlineDiscount.nextTier')}>
                    {ladder.nextTier
                        ? `${t('onlineDiscount.tierLabel', {
                            minimum: formatNumberWithDash(ladder.nextTier.minimumPrice),
                            discount: formatNumberWithDash(ladder.nextTier.discountPrice)
                        })} — ${t('onlineDiscount.toReach', { amount: formatNumberWithDash(ladder.toReachNextTier) })}`
                        : t('onlineDiscount.maxTierReached')}
                </Descriptions.Item>
                <Descriptions.Item label={t('onlineDiscount.discountAmount')}>
                    <Text strong>{baht(ladder.discountAmount)}</Text>
                    {!ladder.applicable &&
                        <Text type='secondary'>{` — ${t('onlineDiscount.ladderNotApplicable')}`}</Text>}
                </Descriptions.Item>
            </Descriptions>
        )
    }

    const renderOffer = offer => {
        if (!offer) return null

        return (
            <Descriptions bordered size='small' column={1} style={{ marginTop: 16 }}
                title={t('onlineDiscount.limitedTimeOffer')}>
                <Descriptions.Item label={t('onlineDiscount.discountPercent')}>
                    {`${offer.discountPercent}%`}
                    {offer.endDate &&
                        <Text type='secondary'>
                            {` (${t('onlineDiscount.until')} ${dayjs(offer.endDate).format('D MMM YYYY')})`}
                        </Text>}
                </Descriptions.Item>
                <Descriptions.Item label={t('onlineDiscount.participatingItemsTotal')}>
                    {baht(offer.participatingItemsTotal)}
                </Descriptions.Item>
                <Descriptions.Item label={t('onlineDiscount.discountAmount')}>
                    <Text strong>{baht(offer.discountAmount)}</Text>
                </Descriptions.Item>
            </Descriptions>
        )
    }

    const renderVoucher = voucher => {
        if (!voucher) return null

        if (!voucher.found) {
            return (
                <Alert style={{ marginTop: 16 }} type='warning' showIcon
                    message={t('onlineDiscount.voucherNotFound', { code: voucher.code })} />
            )
        }

        return (
            <Descriptions bordered size='small' column={1} style={{ marginTop: 16 }}
                title={t('onlineDiscount.voucher')}>
                <Descriptions.Item label={t('onlineDiscount.voucherCode')}>
                    {`${voucher.code} (${voucher.displayVoucherType})`}
                </Descriptions.Item>
                <Descriptions.Item label={t('onlineDiscount.couponDiscount')}>
                    {baht(voucher.couponDiscount)}
                </Descriptions.Item>
                <Descriptions.Item label={t('onlineDiscount.shippingCostDiscount')}>
                    {baht(voucher.shippingCostDiscount)}
                </Descriptions.Item>
                <Descriptions.Item label={t('onlineDiscount.discountAmount')}>
                    <Text strong>{baht(voucher.discountAmount)}</Text>
                </Descriptions.Item>
            </Descriptions>
        )
    }

    const renderSummary = () => (
        <Descriptions bordered size='small' column={1} style={{ marginTop: 16 }}
            title={t('onlineDiscount.summary')}>
            <Descriptions.Item label={t('onlineDiscount.lineItemsTotal')}>
                {`${baht(preview.currentLineItemsTotal)} → `}
                <Text strong>{baht(preview.onlineLineItemsTotal)}</Text>
                {!!preview.lineItemsDiscountTotal &&
                    <Text type='success'>{` (-${baht(preview.lineItemsDiscountTotal)})`}</Text>}
            </Descriptions.Item>
            <Descriptions.Item label={t('onlineDiscount.orderDiscount')}>
                {`${baht(preview.currentOrderDiscount)} → `}
                <Text strong>{baht(preview.onlineOrderDiscount)}</Text>
            </Descriptions.Item>
        </Descriptions>
    )

    const renderBody = () => {
        if (loading) return <Spin style={{ display: 'block', margin: '48px auto' }} />
        if (!preview) return <Empty description={t('onlineDiscount.noPreview')} />

        return (
            <React.Fragment>
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        allowClear
                        onChange={event => setVoucherCode(event.target.value)}
                        onPressEnter={recheck}
                        placeholder={t('onlineDiscount.voucherCodePlaceholder')}
                        value={voucherCode} />
                    <Button onClick={recheck}>{t('onlineDiscount.recheck')}</Button>
                </Space.Compact>

                <Divider orientation='left' style={{ marginTop: 24 }}>
                    {t('onlineDiscount.itemLevel')}
                </Divider>
                <Table
                    columns={columns}
                    dataSource={preview.lineItems}
                    pagination={false}
                    rowKey='lineItemId'
                    size='small' />
                {!!preview.skippedLineItemCount &&
                    <Alert style={{ marginTop: 8 }} type='info' showIcon
                        message={t('onlineDiscount.skippedLineItems', { itemCount: preview.skippedLineItemCount })} />}

                <Divider orientation='left' style={{ marginTop: 24 }}>
                    {t('onlineDiscount.orderLevel')}
                </Divider>
                {renderLadder(preview.discountLadder)}
                {renderOffer(preview.limitedTimeOffer)}
                {renderVoucher(preview.voucher)}

                {renderSummary()}

                <Space direction='vertical' style={{ marginTop: 16 }}>
                    <Checkbox checked={applyLineItemPrices}
                        onChange={event => setApplyLineItemPrices(event.target.checked)}>
                        {t('onlineDiscount.applyLineItemPrices')}
                    </Checkbox>
                    <Checkbox checked={applyOrderDiscount}
                        onChange={event => setApplyOrderDiscount(event.target.checked)}>
                        {t('onlineDiscount.applyOrderDiscount')}
                    </Checkbox>
                </Space>
                {applyOrderDiscount && !!preview.currentOrderDiscount &&
                    <Alert style={{ marginTop: 8 }} type='warning' showIcon
                        message={t('onlineDiscount.overwriteWarning', {
                            amount: formatNumberWithDash(preview.currentOrderDiscount)
                        })} />}
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <Button icon={<TagsOutlined />} onClick={openModal}>
                {t('onlineDiscount.button')}
            </Button>
            <Modal
                cancelText={t('onlineDiscount.cancel')}
                confirmLoading={applying}
                okButtonProps={{ disabled: !preview || (!applyLineItemPrices && !applyOrderDiscount) }}
                okText={t('onlineDiscount.apply')}
                onCancel={() => setOpen(false)}
                onOk={onApply}
                open={open}
                title={t('onlineDiscount.title')}
                width={880}>
                {renderBody()}
            </Modal>
        </React.Fragment>
    )
}

ApplyOnlineDiscount.propTypes = {
    orderId: PropTypes.string.isRequired
}

export default ApplyOnlineDiscount
